/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Select, { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { useMediaQuery } from 'beautiful-react-hooks';

import { usePatientSummary } from '../hooks/ChatContext';

export type KeyValue = {
  id: string;
  name: string;
};

interface RoundedColorSelectProps {
  id: string;
  compact?: boolean;
  className?: string;
  label?: string;
  items: KeyValue[];
  value: string;
  nullValue?: ReactNode;
  onChange: (value: SelectChangeEvent | undefined) => void;
  backgroundColor?: string;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: ReactNode;
  createNewOption?: string;
  infoText?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export function RoundedColorSelect({
  id,
  compact,
  className,
  value,
  label,
  items,
  nullValue,
  onChange,
  onClick,
  disabled,
  placeholder,
  createNewOption,
  infoText,
}: RoundedColorSelectProps) {
  const isSmallScreen = useMediaQuery('(max-width: 500px)') || compact;
  const showPatientSummary = usePatientSummary();

  const RoundedColorSelectTheme = styled(InputBase)(() => ({
    '& .MuiInputBase-input': {
      fontSize: 14,
      fontWeight: 700,
      backgroundColor: 'var(--audienceselectorbackground)',
      color: 'var(--audienceinputcolor)',
      borderRadius: 0,
      padding: '5px 0 5px 12px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--audienceselectorarrow)',
      right: 0,
    },
  }));

  return (
    <FormControl
      disabled={disabled}
      className={className}
      sx={{
        width: 163,
        borderRadius: 2,
        '@media (max-width: 1075px)': {
          width: showPatientSummary ? 130 : 163,
        },
      }}
    >
      <Select
        labelId={`${id}-select-label`}
        id={`${id}-select`}
        value={value || (nullValue ? '_null_' : '')}
        renderValue={() => value || placeholder || nullValue}
        label={label}
        notched={
          undefined
        } /* urgh, a bug somewhere in mui/typescript.  Feel free to remove as long as the unit tests pass after */
        size={isSmallScreen ? 'small' : 'medium'}
        input={
          <RoundedColorSelectTheme
            inputProps={{
              'aria-label': `select ${id.replaceAll('-', ' ')}`,
            }}
          />
        }
        displayEmpty
        IconComponent={ExpandMoreIcon}
        onChange={(x) => {
          onChange(!x.target.value || x.target.value === '_null_' ? undefined : x);
        }}
        onClick={onClick}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: 'var(--audienceselectorbackground)',
              boxShadow: 'none',
              '& .MuiMenuItem-root': {
                fontWeight: 700,
                fontSize: '14px',
                marginLeft: '5px',
                marginRight: '5px',
                '&:hover': {
                  backgroundColor: 'var(--brightgray) ',
                },
                '&.Mui-selected': {
                  // In the absence of this important ,we see a dark gray highlight . We need to avoid this.
                  backgroundColor: 'var(--brightgray) !important',
                  '&:hover': {
                    backgroundColor: 'var(--brightgray) ',
                  },

                  color: 'var(--audienceinputcolor)',
                },
              },
            },
          },
        }}
      >
        {infoText && (
          <div
            style={{
              borderStyle: 'solid none',
              width: '197px',
              textAlign: 'center',
              borderColor: 'rgba(61, 58, 98, 0.08)',
              marginBottom: '6px',
              fontSize: '12px',
              fontStyle: 'italic',
              padding: '0px 27px',
            }}
          >
            {infoText}
          </div>
        )}
        {nullValue && (
          <MenuItem key="_null_" value="_null_">
            {nullValue}
          </MenuItem>
        )}
        {items.map((i) => (
          <MenuItem key={i.id} value={i.name}>
            {i.name}
          </MenuItem>
        ))}
        {createNewOption && (
          <MenuItem key="_create_" value="_create_">
            <i style={{ fontWeight: 400 }}>{createNewOption}</i>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}

export default RoundedColorSelect;
